import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import dayjs, { type Dayjs } from 'dayjs'
import AppDatePicker from '@/components/DatePickers/AppDatePicker'
import ProFilterIcon from '@assets/ProFilter.svg'
import { useAppTheme } from '@/theme/ThemeContext'

export type AdditionalFilterValue = string | Dayjs | null

export type AdditionalFilterField = {
  name: string
  label: string
  type: 'text' | 'select' | 'autocomplete' | 'date'
  options?: string[]
  loading?: boolean
  onOpen?: () => void
  allOptionLabel?: string
  /** Date fields: name of the date field this one must not come before. */
  notBefore?: string
  /** Date fields: name of the date field this one must not come after. */
  notAfter?: string
}

type AdditionalFiltersDialogProps = {
  open: boolean
  fields: AdditionalFilterField[]
  values: Record<string, AdditionalFilterValue>
  onChange: (name: string, value: AdditionalFilterValue) => void
  onApply: () => void
  onClear: () => void
  onClose: () => void
}

export default function AdditionalFiltersDialog({
  open,
  fields,
  values,
  onChange,
  onApply,
  onClear,
  onClose,
}: AdditionalFiltersDialogProps) {
  const { mode } = useAppTheme()
  const isDark = mode === 'dark'

  const colors = {
    background: isDark ? '#202B40' : '#F8FAFD',
    text: isDark ? '#BBD7FF' : '#003F61',
    border: isDark ? '#526B90' : '#DCE2EA',
    fieldBorder: isDark ? '#4B6FB1' : '#C0C2D6',
    activeFieldBorder: isDark ? '#BBD7FF' : '#003F61',
    button: isDark ? '#0B47A8' : '#DDF3FF',
    buttonHover: isDark ? '#0D55C5' : '#CDEEFF',
    buttonText: isDark ? '#FFFFFF' : '#003F61',
    dropdown: isDark ? '#202B40' : '#FFFFFF',
    dropdownHover: isDark ? '#2A3851' : '#EEF5FA',
  }

  const hasValue = (value: AdditionalFilterValue) => value !== '' && value !== null && value !== undefined

  const getFieldSx = (active: boolean) => {
    const outline = active ? colors.activeFieldBorder : colors.fieldBorder

    return {
      minWidth: 0,
      direction: 'rtl',
      '& .MuiOutlinedInput-root': {
        height: 'clamp(44px, 4.5vw, 56px)',
        borderRadius: 0,
        backgroundColor: 'transparent',
        color: colors.text,
        fontFamily: 'Rubik, sans-serif',
        fontSize: 'clamp(13px, 1.05vw, 18px)',
        '& fieldset': { borderColor: `${outline} !important`, borderWidth: '1px' },
        '&:hover fieldset': { borderColor: `${outline} !important` },
        '&.Mui-focused fieldset': {
          borderColor: `${colors.activeFieldBorder} !important`,
          borderWidth: '1px',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        direction: 'rtl',
        textAlign: 'right',
      },
      '& .MuiOutlinedInput-notchedOutline legend': {
        width: 'fit-content !important',
        maxWidth: 'fit-content !important',
        marginLeft: 'auto',
        marginRight: '6px',
        paddingInline: '5px',
        textAlign: 'right',
        fontSize: '10px',
      },
      '& .MuiInputLabel-root': {
        color: `${colors.text} !important`,
        fontFamily: 'Rubik, sans-serif',
        fontSize: 'clamp(12px, 0.9vw, 16px)',
        right: 12,
        left: 'auto',
        transformOrigin: 'top right',
        '&.Mui-focused': { color: `${colors.text} !important` },
      },
      '& .MuiInputLabel-shrink': {
        transform: 'translate(0, -8px) scale(0.75)',
        backgroundColor: colors.background,
        paddingInline: '5px',
      },
      '& .MuiInputBase-input': {
        textAlign: 'right',
        direction: 'rtl',
        fontFamily: 'Rubik, sans-serif',
      },
      '& .MuiSelect-select': {
        textAlign: 'right',
        paddingRight: '12px !important',
        paddingLeft: '34px !important',
      },
      '& .MuiSelect-icon, & .MuiAutocomplete-endAdornment': {
        left: 8,
        right: 'auto',
        color: colors.text,
      },
      '& .MuiIconButton-root': { color: colors.text },
    }
  }

  const renderField = (field: AdditionalFilterField) => {
    const value = values[field.name] ?? ''
    const fieldSx = getFieldSx(hasValue(value))
    const sharedProps = {
      label: field.label,
      size: 'small' as const,
      fullWidth: true,
      InputLabelProps: { shrink: true },
      sx: fieldSx,
    }

    if (field.type === 'date') {
      const limit = (name?: string) => {
        const other = name ? values[name] : null
        return dayjs.isDayjs(other) ? other.toDate() : undefined
      }
      return (
        <AppDatePicker
          key={field.name}
          label={field.label}
          value={dayjs.isDayjs(value) ? value.toDate() : null}
          onChange={next => onChange(field.name, next ? dayjs(next) : null)}
          minDate={limit(field.notBefore)}
          maxDate={limit(field.notAfter)}
          disableFuture
          clearable
          width="100%"
        />
      )
    }

    if (field.type === 'select') {
      return (
        <TextField
          key={field.name}
          {...sharedProps}
          select
          value={typeof value === 'string' ? value : ''}
          onChange={event => onChange(field.name, event.target.value)}
        >
          <MenuItem value="">{field.allOptionLabel ?? 'הכל'}</MenuItem>
          {(field.options ?? []).map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </TextField>
      )
    }

    if (field.type === 'autocomplete') {
      return (
        <Autocomplete<string, false, false, true>
          key={field.name}
          freeSolo
          value={typeof value === 'string' && value ? value : null}
          options={field.options ?? []}
          loading={field.loading}
          onOpen={field.onOpen}
          onChange={(_, nextValue) => onChange(field.name, nextValue ?? '')}
          onInputChange={(_, nextValue, reason) => {
            if (reason === 'input' || reason === 'clear') onChange(field.name, nextValue)
          }}
          sx={fieldSx}
          slotProps={{
            paper: {
              sx: {
                bgcolor: colors.dropdown,
                color: colors.text,
                '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option[aria-selected="true"]': {
                  bgcolor: colors.dropdownHover,
                },
              },
            },
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={field.label}
              size="small"
              fullWidth
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {field.loading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )
    }

    return (
      <TextField
        key={field.name}
        {...sharedProps}
        value={typeof value === 'string' ? value : ''}
        onChange={event => onChange(field.name, event.target.value)}
      />
    )
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        dir="rtl"
        PaperProps={{
          sx: {
            width: 'min(96vw, 1280px)',
            maxWidth: '1280px',
            minHeight: { xs: 'auto', md: 'clamp(440px, 46vw, 560px)' },
            maxHeight: '90vh',
            m: 1,
            bgcolor: `${colors.background} !important`,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            boxShadow: isDark ? '0 24px 55px rgba(0,0,0,.42)' : '0 24px 55px rgba(0,63,97,.14)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ px: { xs: 2, md: 'clamp(24px, 2.5vw, 44px)' }, pt: 'clamp(18px, 1.8vw, 30px)', pb: 'clamp(14px, 1.4vw, 22px)', bgcolor: colors.background }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${colors.border}`, pb: 'clamp(14px, 1.4vw, 22px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box component="img" src={ProFilterIcon} alt="" sx={{ width: 'clamp(20px, 1.7vw, 28px)', height: 'clamp(20px, 1.7vw, 28px)' }} />
              <Typography sx={{ color: colors.text, fontFamily: 'Rubik, sans-serif', fontSize: 'clamp(16px, 1.35vw, 22px)', fontWeight: 400 }}>
                סינונים נוספים
              </Typography>
            </Box>
            <IconButton aria-label="סגירה" onClick={onClose} sx={{ color: colors.text, p: 0.25 }}>
              <ClearIcon sx={{ fontSize: 'clamp(24px, 2vw, 34px)' }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, md: 'clamp(24px, 2.5vw, 44px)' }, py: 'clamp(10px, 1vw, 18px)', bgcolor: colors.background }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: { xs: 1.5, md: 'clamp(20px, 2.2vw, 32px) clamp(16px, 2vw, 28px)' }, width: { xs: '100%', md: '72%' }, mx: 'auto', pt: 0.5, alignContent: 'start' }}>
            {fields.map(renderField)}
          </Box>
        </DialogContent>

        <DialogActions sx={{ display: 'block', px: { xs: 2, md: 'clamp(24px, 2.5vw, 44px)' }, pt: 'clamp(12px, 1.2vw, 20px)', pb: 'clamp(16px, 1.8vw, 28px)', bgcolor: colors.background }}>
          <Box sx={{ borderTop: `1px solid ${colors.border}`, pt: 'clamp(12px, 1.2vw, 20px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', direction: 'ltr' }}>
            <Button variant="contained" onClick={onApply} sx={{ minWidth: 'clamp(84px, 8vw, 132px)', height: 'clamp(38px, 3.2vw, 54px)', borderRadius: '4px', bgcolor: colors.button, color: colors.buttonText, border: `1px solid ${isDark ? '#1559B7' : '#C8E8F7'}`, boxShadow: 'none', fontFamily: 'Rubik, sans-serif', fontSize: 'clamp(13px, 1vw, 17px)', '&:hover': { bgcolor: colors.buttonHover, boxShadow: 'none' } }}>
              חיפוש
            </Button>
            <Button onClick={onClear} sx={{ color: colors.text, fontFamily: 'Rubik, sans-serif', fontSize: 'clamp(13px, 1vw, 17px)', fontWeight: 300, '&:hover': { backgroundColor: 'transparent', color: isDark ? '#81B5FF' : '#72A4EB' } }}>
              ניקוי סינונים
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}
